<script>
  import { appIconUrl } from "lib/util/appIcons.js";
  import { onMount } from "svelte";
  import { DataTableSkeleton } from "carbon-components-svelte";
  import Header from "lib/components/Header.svelte";
  import PaxbotChat from "lib/components/paxbot/PaxbotChat.svelte";
  import { SectionWrapper } from "paxray-components-svelte";
  import ApplicationCountTrendCard from "./components/ApplicationCountTrendCard.svelte";
  import CategoryFootprintCard from "./components/CategoryFootprintCard.svelte";
  import LeadingApplicationsCard from "./components/LeadingApplicationsCard.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { ROUTES } from "@utils/routes.js";
  import { useNavigate } from "svelte-navigator";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";

  const navigate = useNavigate();
  const softwarePortfolioHelpContext = {
    task: "Understand the state of the software portfolio and identify the most important management actions.",
    starterQuestions: [
      "What needs management attention?",
      "How is the portfolio developing?",
      "Where can we harmonize tools?",
      "Which governance gaps matter most?",
    ],
  };
  let catalogData = { applications: [], summary: {} };
  let analysisData = { history: [], changes: [] };
  let deepDiveData = { categories: [], harmonization_candidates: [] };
  let maturity = { applicationCount: null, score: 0, tone: "warning", groups: [] };
  let loading = true;
  let error = "";
  let paxbotOpen = false;
  let hoveredTrendPoint = null;

  $: applications = catalogData.applications || [];
  $: summary = catalogData.summary || {};
  $: history = analysisData.history || [];
  $: maturity = buildMaturity(applications, summary);
  $: managementActions = buildManagementActions(
    applications,
    analysisData.changes || [],
    deepDiveData.harmonization_candidates || [],
  );
  $: topCategories = buildCategoryFootprint(applications, summary).slice(0, 10);
  $: topApplications = buildTopApplications(applications, summary).slice(0, 10);
  $: demoApplicationTrend = Boolean(
    $uiFeatureStore.platformFeatures.demoSoftwareIntelligence,
  );
  $: applicationTrend = buildApplicationTrend(history, summary);
  $: executiveBrief = buildExecutiveBrief(
    maturity,
    summary,
    managementActions,
  );

  onMount(loadDashboard);

  async function loadDashboard() {
    loading = true;
    error = "";
    try {
      const [catalog, analysis, deepDive] = await Promise.all([
        fetchJson("/api/monitoring/catalog"),
        fetchJson("/api/monitoring/analysis"),
        fetchJson("/api/monitoring/category-deep-dive"),
      ]);
      catalogData = catalog;
      analysisData = analysis;
      deepDiveData = deepDive;
    } catch (loadError) {
      error =
        loadError.message || "Software Intelligence overview could not be loaded.";
    } finally {
      loading = false;
    }
  }

  async function fetchJson(url) {
    const response = await fetchWithSession(url);
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.message || "Management data could not be loaded.");
    }
    return result;
  }

  function dashboardIconUrl(row) {
    return appIconUrl(row?.application, { clearname: row?.clearname });
  }

  function formatNumber(value) {
    if (value === null || value === undefined) return "N/A";
    return new Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(Number(value));
  }

  function formatDuration(seconds) {
    if (seconds === null || seconds === undefined) return "N/A";
    const hours = Number(seconds) / 3600;
    if (hours >= 1000) {
      return `${new Intl.NumberFormat("en", {
        maximumFractionDigits: 1,
      }).format(hours / 1000)}k h`;
    }
    return `${new Intl.NumberFormat("en", {
      maximumFractionDigits: hours < 10 ? 1 : 0,
    }).format(hours)} h`;
  }

  function formatPercent(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "N/A";
    }
    const number = Number(value);
    if (number > 0 && number < 1) return "<1%";
    return `${new Intl.NumberFormat("en", {
      maximumFractionDigits: number < 10 ? 1 : 0,
    }).format(number)}%`;
  }

  function formatPlainNumber(value) {
    if (value === null || value === undefined) return "N/A";
    return new Intl.NumberFormat("en").format(Number(value));
  }

  function share(value, total) {
    const totalValue = Number(total || 0);
    if (!totalValue) return null;
    return Number(value || 0) / totalValue * 100;
  }

  function formatDate(value) {
    if (!value) return "N/A";
    const date = new Date(value);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  }

  function buildMaturity(items, currentSummary) {
    const safeItems = Array.isArray(items) ? items : [];
    const safeSummary = currentSummary && typeof currentSummary === "object"
      ? currentSummary
      : {};
    const metrics = buildMaturityMetrics(safeItems, safeSummary);
    const components = [
      {
        key: "topApp",
        label: "Top App",
        value: metrics.topAppShare,
        scoreValue: metrics.topAppScore,
        tooltip: `Top app share by active time. Top app: ${metrics.topApplication?.application || "N/A"}.`,
      },
      {
        key: "mainAppShare",
        label: "Main App Share",
        value: metrics.mainAppShare,
        scoreValue: metrics.mainAppShare,
        tooltip: "Share of active time assigned to main applications.",
      },
      {
        key: "communicationAppShare",
        label: "Communication App Share",
        value: metrics.communicationAppShare,
        scoreValue: metrics.communicationAppShare,
        tooltip: "Share of active time in communication or collaboration categories.",
      },
      {
        key: "unwantedAppShare",
        label: "Unwanted App Share",
        value: metrics.unwantedAppShare,
        scoreValue:
          metrics.unwantedAppShare === null ? null : 100 - metrics.unwantedAppShare,
        tooltip: "Share of active time assigned to unwanted applications. Lower share improves the score.",
      },
      {
        key: "applicationVariance",
        label: "Application Variance",
        value: metrics.applicationVarianceScore,
        scoreValue: metrics.applicationVarianceScore,
        tooltip: "Normalized active-time distribution across applications. Higher means usage is less concentrated.",
      },
      {
        key: "appsPerUser",
        label: "Apps per User",
        value: metrics.appsPerUser,
        valueFormatter: (value) => Number(value).toFixed(1),
        scoreValue: metrics.appsPerUserScore,
        tooltip: "Average applications per user, capped at 10 apps per user for this score component.",
      },
    ];
    const groups = [
      {
        key: "usageBalance",
        label: "Usage Concentration",
        componentKeys: ["topApp", "mainAppShare"],
      },
      {
        key: "portfolioMix",
        label: "Portfolio Mix",
        componentKeys: ["communicationAppShare", "unwantedAppShare"],
      },
      {
        key: "applicationBreadth",
        label: "Application Breadth",
        componentKeys: ["applicationVariance", "appsPerUser"],
      },
    ].map((group) => {
      const groupComponents = components.filter((component) =>
        group.componentKeys.includes(component.key),
      );
      const validComponents = groupComponents.filter((component) =>
        component.scoreValue !== null &&
        component.scoreValue !== undefined &&
        !Number.isNaN(Number(component.scoreValue)),
      );
      const score = validComponents.length
        ? Math.round(
            validComponents.reduce(
              (total, component) => total + Number(component.scoreValue),
              0,
            ) / validComponents.length,
          )
        : null;
      return {
        ...group,
        score,
        tooltip: validComponents.length
          ? validComponents
              .map((component) => `${component.label}: ${formatMaturityValue(component)}`)
              .join(" · ")
          : "No data available for this score.",
      };
    });
    const validGroups = groups.filter((group) => group.score !== null);
    const score = validGroups.length
      ? Math.round(
          validGroups.reduce((total, group) => total + group.score, 0) /
            validGroups.length,
        )
      : 0;

    return {
      score,
      applicationCount: metrics.applicationCount,
      users: metrics.users,
      appsPerUser: metrics.appsPerUser,
      unwantedShare: metrics.unwantedAppShare || 0,
      topShare: metrics.topAppShare || 0,
      components,
      groups,
      tone: score >= 70 ? "positive" : score >= 40 ? "warning" : "negative",
    };
  }

  function buildMaturityMetrics(items, currentSummary) {
    const sortedItems = [...items].sort(
      (a, b) =>
        Number(b.active_time_seconds || 0) - Number(a.active_time_seconds || 0),
    );
    const applicationCount = Number(currentSummary.application_count || sortedItems.length || 0);
    const activeTime = Number(currentSummary.active_time_seconds || 0);
    const topApplication = sortedItems[0];
    const topAppShare = share(topApplication?.active_time_seconds, activeTime);
    const mainTime = sumByMatcher(sortedItems, (item) => normalizeType(item.type) === "main");
    const communicationTime = sumByMatcher(sortedItems, (item) =>
      /communication|communicat|collaboration|email|chat|meeting/i.test(
        `${item.category || ""} ${item.type || ""}`,
      ),
    );
    const unwantedTime = sumByMatcher(sortedItems, (item) => normalizeType(item.type) === "unwanted");
    const applicationVarianceScore = buildApplicationVarianceScore(sortedItems, activeTime);
    const appsPerUser =
      currentSummary.average_apps_per_user === null ||
      currentSummary.average_apps_per_user === undefined
        ? null
        : Number(currentSummary.average_apps_per_user);

    return {
      applicationCount,
      users: currentSummary.users,
      topApplication,
      topAppShare,
      topAppScore: topAppShare === null ? null : Math.min(topAppShare / 40 * 100, 100),
      mainAppShare: share(mainTime, activeTime),
      communicationAppShare: share(communicationTime, activeTime),
      unwantedAppShare: share(unwantedTime, activeTime),
      applicationVarianceScore,
      appsPerUser,
      appsPerUserScore: appsPerUser === null ? null : Math.min(appsPerUser / 10 * 100, 100),
    };
  }

  function buildApplicationVarianceScore(items, activeTime) {
    if (!activeTime || items.length <= 1) return null;
    const concentration = items.reduce((total, item) => {
      const itemShare = Number(item.active_time_seconds || 0) / activeTime;
      return total + itemShare * itemShare;
    }, 0);
    const minimumConcentration = 1 / items.length;
    return (1 - concentration) / (1 - minimumConcentration) * 100;
  }

  function formatMaturityValue(component) {
    if (component.value === null || component.value === undefined) return "N/A";
    if (component.valueFormatter) return component.valueFormatter(component.value);
    return formatPercent(component.value);
  }

  function sumByMatcher(items, matcher) {
    return items
      .filter(matcher)
      .reduce((total, item) => total + Number(item.active_time_seconds || 0), 0);
  }

  function buildApplicationTrend(items, currentSummary) {
    let snapshots = items
      .map((snapshot) => ({
        date: snapshot.imported_at,
        value: snapshot.summary?.application_count,
      }))
      .filter((point) => point.value !== null && point.value !== undefined);

    if (demoApplicationTrend && Number(currentSummary.application_count || 0) > 0) {
      const currentCount = Number(currentSummary.application_count);
      const demoStart = new Date();
      demoStart.setMonth(demoStart.getMonth() - 24);
      const demoFactors = [1.55, 1.49, 1.49, 1.42, 1.36, 1.36, 1.23, 1.23, 1];
      snapshots = Array.from({ length: 9 }, (_, index) => {
        const date = new Date(demoStart);
        date.setMonth(demoStart.getMonth() + index * 3);
        return {
          date: date.toISOString(),
          value: Math.round(currentCount * demoFactors[index]),
        };
      });
    }
    if (!snapshots.length && currentSummary.application_count !== undefined) {
      snapshots.push({
        date: null,
        value: currentSummary.application_count,
      });
    }
    if (!snapshots.length) {
      return { points: [], line: "", area: "", startLabel: "N/A", endLabel: "N/A" };
    }
    const max = Math.max(...snapshots.map((point) => Number(point.value)));
    const min = Math.min(...snapshots.map((point) => Number(point.value)));
    const range = max - min || 1;
    const points = snapshots.map((point, index) => {
      const x = snapshots.length === 1 ? 240 : index / (snapshots.length - 1) * 480;
      const y = 115 - (Number(point.value) - min) / range * 90;
      return {
        ...point,
        x,
        y,
      };
    });
    const line = points.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
    return {
      points,
      line,
      area: points.length > 1 ? `0,130 ${line} 480,130` : "",
      startLabel: formatDate(points[0]?.date),
      endLabel: formatDate(points[points.length - 1]?.date),
    };
  }

  function buildCategoryFootprint(items, currentSummary) {
    const categories = new Map();
    items.forEach((item) => {
      const category = item.category || "N/A";
      const current = categories.get(category) || {
        category,
        active_time_seconds: 0,
        users: null,
        applications: 0,
      };
      current.active_time_seconds += Number(item.active_time_seconds || 0);
      current.applications += 1;
      current.users =
        current.users === null && item.users === undefined
          ? null
          : Number(current.users || 0) + Number(item.users || 0);
      categories.set(category, current);
    });
    return [...categories.values()]
      .map((category) => ({
        ...category,
        share: share(category.active_time_seconds, currentSummary.active_time_seconds),
      }))
      .sort((a, b) => b.active_time_seconds - a.active_time_seconds);
  }

  function buildTopApplications(items, currentSummary) {
    return [...items]
      .sort(
        (a, b) =>
          Number(b.active_time_seconds || 0) - Number(a.active_time_seconds || 0),
      )
      .map((application, index) => ({
        ...application,
        id: application.id || `${application.application || "application"}-${index}`,
        vendor: application.vendor || application.manufacturer || "",
        appName: application.application,
        share: share(application.active_time_seconds, currentSummary.active_time_seconds),
      }));
  }

  function normalizeType(value) {
    return String(value || "").trim().toLowerCase();
  }

  function buildManagementActions(items, changes, candidates) {
    const actions = [];
    const growingUnwanted = changes.filter(
      (item) =>
        String(item.type || "").toLowerCase() === "unwanted" &&
        Number(item.changes?.active_time_seconds || 0) > 20,
    );
    const unclassified = items.filter(
      (item) =>
        !item.type ||
        String(item.type).toLowerCase() === "unclassified",
    );
    const copyFriction = [...items]
      .filter((item) => Number(item.copy_time_seconds || 0) > 0)
      .sort(
        (a, b) =>
          Number(b.copy_time_seconds || 0) -
          Number(a.copy_time_seconds || 0),
      )[0];
    const harmonization = candidates[0];

    if (growingUnwanted.length) {
      actions.push({
        priority: "High",
        tone: "risk",
        title: `Review ${growingUnwanted.length} growing unwanted app${growingUnwanted.length === 1 ? "" : "s"}`,
        detail: growingUnwanted
          .slice(0, 2)
          .map((item) => item.application)
          .join(", "),
        route: ROUTES.monitoring.changeAnalysis,
      });
    }
    if (harmonization) {
      actions.push({
        priority:
          String(harmonization.priority || "medium").toLowerCase() === "high"
            ? "High"
            : "Medium",
        tone: "opportunity",
        title: `Validate ${harmonization.tools?.[0]} ↔ ${harmonization.tools?.[1]}`,
        detail: `${Number(harmonization.team_overlap_percent || 0).toFixed(0)}% team overlap`,
        route: ROUTES.monitoring.categoryDeepDive,
      });
    }
    if (unclassified.length) {
      actions.push({
        priority: "Medium",
        tone: "governance",
        title: `Classify ${unclassified.length} application${unclassified.length === 1 ? "" : "s"}`,
        detail: "Improve ownership, policy and portfolio transparency.",
        route: ROUTES.monitoring.catalog,
      });
    }
    if (copyFriction) {
      actions.push({
        priority: "Explore",
        tone: "information",
        title: `Inspect interface friction around ${copyFriction.application}`,
        detail: `${formatDuration(copyFriction.copy_time_seconds)} annual copy time`,
        route: ROUTES.monitoring.catalog,
      });
    }
    return actions.slice(0, 4);
  }

  function buildExecutiveBrief(portfolioMaturity, currentSummary, actions) {
    if (!Number(currentSummary.application_count || 0)) {
      return "Transfer calculated project results to establish the company-wide Software Intelligence baseline.";
    }
    const direction =
      Number(currentSummary.changes?.active_time_seconds || 0) > 0
        ? "expanded"
        : Number(currentSummary.changes?.active_time_seconds || 0) < 0
          ? "contracted"
          : "remained stable";
    return `The observed software footprint ${direction} versus the previous data update. Portfolio maturity is ${portfolioMaturity.score}/100, with ${actions.length} management action${actions.length === 1 ? "" : "s"} currently prioritized.`;
  }

  async function requestPaxbotAnswer(question, historyItems, responseLanguage) {
    const response = await fetchWithSession("/api/damprojects/paxbot/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question,
        history: historyItems.slice(-4),
        context: {
          page: "software intelligence portfolio",
          responseLanguage,
          executiveBrief,
          portfolioMaturity: maturity,
          summary,
          managementActions,
          topCategories,
          topApplications: topApplications.map((item) => ({
            application: item.application,
            activeTime: formatDuration(item.active_time_seconds),
            users: item.users,
            type: item.type,
          })),
        },
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status === "error") {
      throw new Error(result.message || "Pax AI request failed");
    }
    return result.answer;
  }
</script>

<Header
  section="Digital Twin"
  projectNameOverride="Digital Twin"
  projectHref={ROUTES.dam.root}
  area="Software Intelligence"
  areaClickable={false}
  breadcrumb="Software Portfolio"
  tooltipText="Company-wide Software Intelligence portfolio view. It combines transferred project results into portfolio-level KPIs, governance signals, usage trends, category insights and concrete management actions."
></Header>

<SectionWrapper
  fitScreen
  fullscreen
  seamless
  {loading}
  loadingTitle="Loading software portfolio."
  loadingHint="Please wait while the Software Portfolio is prepared."
  helpContext={softwarePortfolioHelpContext}
  wrapperClass="management-overview-wrapper"
  canvasClass="management-overview-canvas"
>
  <div class="management-main" slot="main">
    <PaxbotChat
      bind:open={paxbotOpen}
      heading="Pax AI"
      subheading="Software Intelligence Portfolio Assistant"
      requestAnswer={requestPaxbotAnswer}
      starterQuestions={[
        "What needs management attention?",
        "How is the portfolio developing?",
        "Where can we harmonize tools?",
        "Which governance gaps matter most?",
      ]}
    />

    {#if loading}
      <DataTableSkeleton
        rows={8}
        headers={[
          { key: "metric", value: "Metric" },
          { key: "value", value: "Value" },
        ]}
        showToolbar={false}
      />
    {:else if error}
      <div class="empty-state error">{error}</div>
    {:else}
      <div class="portfolio-grid">
        <CategoryFootprintCard
          categories={topCategories}
          {formatDuration}
          {formatNumber}
          {formatPercent}
        />
        <LeadingApplicationsCard
          applications={topApplications}
          getIconUrl={dashboardIconUrl}
          {formatDuration}
          {formatNumber}
        />
      </div>

      <div class="executive-grid">
        <ApplicationCountTrendCard
          {applicationTrend}
          bind:hoveredTrendPoint
          {formatPlainNumber}
          {formatDate}
          demo={demoApplicationTrend}
        />
      </div>
    {/if}
  </div>

</SectionWrapper>

<style>
  .management-main {
    position: relative;
    display: grid;
    width: 100%;
    min-width: 0;
    height: 100%;
    min-height: 100%;
    grid-template-rows: auto minmax(0, 1fr);
    row-gap: 0;
    overflow: auto;
    scrollbar-width: auto;
  }

  :global(.management-overview-wrapper .view_wrapper) {
    background: var(--pax-smoke);
  }

  :global(.management-overview-wrapper .management-overview-canvas) {
    background: var(--pax-smoke);
    padding: 1rem;
  }

  .management-main::-webkit-scrollbar {
    width: 0.6rem;
  }

  .executive-grid {
    display: grid;
    min-height: 0;
    height: 100%;
    align-items: stretch;
    box-sizing: border-box;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    padding-top: 0;
  }

  .portfolio-grid {
    position: relative;
    display: grid;
    min-height: 0;
    align-items: stretch;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--border-color);
  }

  .portfolio-grid::before {
    position: absolute;
    z-index: 1;
    top: 4rem;
    right: 0;
    left: 0;
    height: 1px;
    background: var(--border-color);
    content: "";
  }

  .portfolio-grid::after {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    background: var(--border-color);
    content: "";
  }

  .empty-state {
    display: grid;
    min-height: 20rem;
    flex: 1;
    place-items: center;
    color: #6f7681;
  }

  .empty-state.error {
    color: #da1e28;
  }

  .management-main :global(.paxbot_chat_window) {
    position: absolute !important;
    z-index: 50 !important;
    right: 1rem !important;
    bottom: 5.85rem !important;
  }

  .management-main :global(.paxbot_chat_mascot) {
    position: absolute !important;
    z-index: 51 !important;
    right: 1.55rem !important;
    bottom: -0.65rem !important;
  }

  @media (max-width: 1250px) {
    .executive-grid {
      grid-template-columns: 1fr;
    }

  }

  @media (max-width: 900px) {
    .management-main {
      display: flex;
      flex-direction: column;
    }

    .executive-grid,
    .portfolio-grid {
      grid-template-columns: 1fr;
    }

    .portfolio-grid::after {
      display: none;
    }

  }
</style>
